import React, { useEffect, useState } from 'react';
import { FuseNavigation, FuseUtils } from '@fuse';
import clsx from 'clsx';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/styles';
import axios from 'axios';
import { API_BASE_URL } from '@config';
import { getLmsConfig } from 'app/main/settings/store/actions/lms.actions';
import { takePendingSsoRequest } from 'app/main/watsonx/watsonxHandoff';

const useStyles = makeStyles(()=> ({
	root: {
		'overflow-x': 'hidden'
	}
}));

const Navigation = props => {
	const navigation = useSelector(({ fuse }) => fuse.navigation);
	const user = useSelector(({ auth }) => auth.user);
	const dispatch = useDispatch();
	const classes = useStyles();

	// "Heidi LMS" shows for an org admin OR an INSTRUCTOR - most professors are
	// instructors, and gating authoring on admin alone kept the people who
	// actually teach off the LMS. Super-admins carry the 'admin' role too, so the
	// role-based nav auth alone cannot exclude them; we mirror the backend rule
	// (organizationId + a teaching seat + NOT super-admin).
	const roles = (user && Array.isArray(user.role)) ? user.role : [];
	const isSuperAdmin = roles.includes('super-admin') || roles.includes('superAdmin');
	const hasLmsSeat = !!(user && user.organizationId && !isSuperAdmin
		&& (roles.includes('admin') || roles.includes('instructor')));

	// Whether to offer it is asked PER ORGANIZATION, not per installation: the
	// Heidi-side switch is one Settings document for the whole control plane, so
	// on a shared box turning it on for one client used to light the menu up for
	// every org admin on it. /api/lms/status folds the installation switch, the
	// portal link and that org's own lms_enabled into one answer.
	//
	// null until the answer arrives, and null HIDES the item here (unlike Imaging
	// below): an LMS menu that leads to "not enabled for your organization" is
	// the exact dead end this replaces.
	const [lmsEnabled, setLmsEnabled] = useState(null);
	useEffect(() => {
		let cancelled = false;
		if (!hasLmsSeat) { setLmsEnabled(false); return undefined; }
		dispatch(getLmsConfig());
		axios.get(`${API_BASE_URL}/api/lms/status`, { skipForbiddenToast: true })
			.then((r) => { if (!cancelled) setLmsEnabled(!!(r.data && r.data.enabled)); })
			.catch(() => { if (!cancelled) setLmsEnabled(false); });
		return () => { cancelled = true; };
	}, [dispatch, hasLmsSeat]);

	// "Imaging" is a lens on DICOM series and scientific image stacks. With
	// neither it is an empty screen that cannot be filled from there, so it is
	// hidden - unlike Storage itself, which stays visible because that is how
	// someone discovers the feature.
	//
	// null until the answer arrives, and null SHOWS the item. Treating "not yet
	// known" as "none" would blink the entry out of the menu on every page load,
	// and hide it permanently for anyone whose check failed.
	const [hasImaging, setHasImaging] = useState(null);
	useEffect(() => {
		let cancelled = false;
		axios.get(`${API_BASE_URL}/api/storage/feature`, { skipForbiddenToast: true })
			.then((r) => { if (!cancelled) setHasImaging(!!(r.data && r.data.hasImaging)); })
			.catch(() => { /* leave it null: show the item rather than lose it */ });
		return () => { cancelled = true; };
	}, []);


	// The IBM watsonx items in "AI & Data" show only where the integration is on
	// for the installation AND this user's organization (Documents, also in that
	// section, always shows). null until the answer arrives, and null HIDES them:
	// every one opens IBM Cloud, so an item that only leads to "not enabled" is
	// noise.
	const [watsonxEnabled, setWatsonxEnabled] = useState(null);
	useEffect(() => {
		let cancelled = false;
		axios.get(`${API_BASE_URL}/api/watsonx/menu`, { skipForbiddenToast: true })
			.then((r) => { if (!cancelled) setWatsonxEnabled(!!(r.data && r.data.enabled)); })
			.catch(() => { if (!cancelled) setWatsonxEnabled(false); });
		return () => { cancelled = true; };
	}, []);

	// An IBM-initiated sign-in that arrived before the user logged in (see
	// watsonxHandoff.js): the navbar mounts once they are in, so resume it here.
	const history = useHistory();
	useEffect(() => {
		const pending = takePendingSsoRequest();
		if (pending && window.location.pathname.indexOf('/watsonx/sso/') !== 0) {
			history.push('/watsonx/sso/' + pending);
		}
	}, [history]);

	let nav = navigation;
	if (!watsonxEnabled) {
		['watsonx-data', 'watsonx-models', 'watsonx-workflows'].forEach((id) => {
			nav = FuseUtils.removeNavItem(nav, id);
		});
	}
	if (!(hasLmsSeat && lmsEnabled)) nav = FuseUtils.removeNavItem(nav, 'heidi-lms');
	if (hasImaging === false) nav = FuseUtils.removeNavItem(nav, 'storage-imaging');
	// The Storage section stays VISIBLE even where the feature is off or not in
	// the organization's plan. Discovery is the point: this is how an admin
	// learns the feature exists, and each page explains what to do about it -
	// activate it, or ask whoever can. Only the pages change, never the menu.

	return (
		<FuseNavigation
			className={clsx(classes.root, 'navigation', props.className)}
			navigation={nav}
			layout={props.layout}
			dense={props.dense}
		/>
	);
};

Navigation.defaultProps = {
	layout: 'vertical'
};

export default Navigation;
